import { useEffect, useState } from "react";
import * as speechsdk from "microsoft-cognitiveservices-speech-sdk";
import { AudioConfig } from "microsoft-cognitiveservices-speech-sdk";
import { useAppContext } from "../appContext";
import { getTokenOrRefresh, updateFeedbackAccessibility } from "../api";
import Nav from "./nav";
import ResizeFeedback from "./ResizeFeedback";
import FeedbackLoader from "./FeedbackLoader";
import { formatTime } from "@/utils/utils";
import { FeedbackType } from "@/utils/types";
import { feedbackMap, generateGUID } from "@/utils/utils";

function InterviewScreen({ isAdminView }: { isAdminView?: boolean }) {
  const { state, reducers, startFunc, gpt, stopAudio, navigateThenRun, setQs } =
    useAppContext();
  const {
    updatesession,
    setrecognizedsp,
    setprocessing,
    setshowApiError,
    setErrorText,
    setNextFunc,
    setshowNextConfirmation,
    setspeechLen,
    sethitMaxLen,
    setcanStop,
    setfeedbackcontext,
    setrole,
    settype,
    setqType,
    setcurrentQuestion,
    setretryPast,
    setprofileUser,
    setpastInfo,
  } = reducers;

  const [showLoader, setShowLoader] = useState(false);
  const [recognizer, setrecognizer] =
    useState<speechsdk.SpeechRecognizer | null>(null);
  const [startTimer, setStartTimer] = useState<boolean>(false);
  const [sessionStopped, setsessionStopped] = useState<boolean>(false);
  const [micListening, setmicListening] = useState<boolean>(false);
  const [isRetry, setisRetry] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [rsp, setrsp] = useState<string>(""); // recognizedspeech in interviewscreen
  const [rspLive, setrspLive] = useState<string>("");
  const [intInterval, setIntInterval] = useState<NodeJS.Timeout | undefined>(
    undefined
  );
  const [micActivationTimeout, setmicActivationTimeout] =
    useState<NodeJS.Timeout | null>(null);

  let audioElement: HTMLAudioElement | null = document.getElementById(
    "audioElement"
  ) as HTMLAudioElement | null;
  let micTimeout: NodeJS.Timeout | null = null;

  const MAXCALLS = 12;
  const token = new URLSearchParams(window.location.search).get('candidate')||"";
  const stopMic = (): void => {
    if (micListening === true) {
      if (micTimeout) {
        clearTimeout(micTimeout);
        micTimeout = null;
      }
      setmicListening(false);
      if (recognizer) {
        // Wait for final recognition before stopping
        setTimeout(() => {
          recognizer.stopContinuousRecognitionAsync();
        }, 500);
      }
      setprocessing(true);
    }
  };

  const sttFromMic = (): void => {
    if (!startTimer) {
      setStartTimer(true);
    }
    setrsp("");
    setmicListening(true);
    setsessionStopped(false);
  };

  useEffect(() => {
    if (isRetry || (state.currentQuestion >= 0 && state.session.length > 0)) {
      if (state.session[state.currentQuestion].feedback === "") {
        synthesizeSpeech(
          state.session[state.currentQuestion].question.question
        );
      }
      setrecognizedsp("");
      setrsp("");
      setisRetry(false);
    }
  }, [state.currentQuestion, isRetry]);

  useEffect(() => {
    if (rsp !== "") {
      let l = state.speechLen + rsp.split(" ").length + 1;
      //console.log(l);
      if (l + state.contextLength > 5000) {
        // 5000 is true max, giving room for gpt 68 words response and also any final words user has.
        sethitMaxLen(true);
      }
      setspeechLen(l);
      setrspLive("");
    }
    setrecognizedsp(rsp);
  }, [rsp]);

  useEffect(() => {
    if (rspLive !== "") {
      let prompt1 = [...state.session[state.currentQuestion].prompt];
      if (prompt1.length > 0 && prompt1[prompt1.length - 1].role == "user") {
        prompt1[prompt1.length - 1] = {
          role: "user",
          content: `${rsp} ${rspLive}`,
        };
      } else {
        prompt1.push({ role: "user", content: `${rsp} ${rspLive}` });
      }
      updatesession({ prompt: prompt1 });
    }
  }, [rspLive]);

  // Useful function
  useEffect(() => {
    if (
      !startTimer &&
      state.session[state.currentQuestion]?.code !== "" &&
      state.session[state.currentQuestion]?.feedback === ""
    ) {
      setStartTimer(true);
    }
  }, [state.session[state.currentQuestion]?.code]);

  // only for interview screen not past feedback
  useEffect(() => {
    if (state.showFeedback) {
      setStartTimer(false);
      if (state.session[state.currentQuestion]?.feedback !== "") {
        setShowLoader(false);
      }
    }
    const fetchFeedback = async () => {
      if (state.session.length > 0 && state.showFeedback) {
        const feed = await feedbackMap(
          state.session[state.currentQuestion].feedback,
          true
        );
        if (!feed) {
          setfeedbackcontext([]);
          return;
        }

        const finalFeedback: FeedbackType = [];
        for (let i = 0; i < feed[0].length; i++) {
          finalFeedback.push({
            title: feed[0][i],
            badgeType: feed[1][i] as any,
            desc: feed[2][i],
          });
        }

        setfeedbackcontext(finalFeedback);
      }
    };
    if (state.showFeedback) {
      fetchFeedback();
    }
  }, [
    state.session[state.currentQuestion]?.feedback,
    state.showFeedback,
    state.currentQuestion,
  ]);

  const updateAccessibleToOrg = async (e: boolean): Promise<void> => {
    const access = state.pastInfo["orgAccessible"];
    if (access === undefined || access !== e) {
      const user2 = await updateFeedbackAccessibility(
        e,
        state.session[state.currentQuestion].question.question,
        state.pastInfo["index"]
      );
      setprofileUser(user2);
      setpastInfo({ ...state.pastInfo, orgAccessible: e });
    }
  };

  const NewQuestionClick = (ind: number) => {
    if (state.isAssessment) return;
    if (rsp === "" || state.session[state.currentQuestion].feedback !== "") {
      setStartTimer(false);
      startFunc(ind, false);
    } else {
      setNextFunc(() => {
        setStartTimer(false);
        startFunc(ind, false);
      });
      setshowNextConfirmation(true);
    }
  };

  const synthesizeSpeech = async (text: string): Promise<void> => {
    // avoid overlay of audio
    if (state.canStop) {
      stopAudio();
    }
    if (state.userAudio) {
      const tokenObj = await getTokenOrRefresh();
      if (
        !state.abortController.signal.aborted &&
        tokenObj.authToken &&
        tokenObj.region
      ) {
        const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
          tokenObj.authToken,
          tokenObj.region
        );

        speechConfig.speechSynthesisLanguage = "en-US";
        speechConfig.speechSynthesisVoiceName = "en-US-MichelleNeural";

        const speechSynthesizer = new speechsdk.SpeechSynthesizer(
          speechConfig,
          AudioConfig.fromStreamOutput(
            speechsdk.AudioOutputStream.createPullStream()
          )
        );
        setcanStop(true);
        return speechSynthesizer.speakTextAsync(
          text === ""
            ? state.interviewer.replace(/<br\/>/g, "")
            : text.replace(/<br\/>/g, "").replace(/\\/g, ""),
          (result: any) => {
            if (result) {
              speechSynthesizer.close();
              const blob = new Blob([result.audioData], { type: "audio/wav" });
              const blobUrl = URL.createObjectURL(blob);
              if (audioElement) {
                audioElement.src = blobUrl;
                audioElement.play();
              }
              return result.audioData;
            }
          },
          (error: string) => {
            setcanStop(false);
            console.log(error);
            speechSynthesizer.close();
          }
        );
      }
    }
  };

  useEffect(() => {
    const handleSessionStopped = async () => {
      if (sessionStopped && !micListening) {
        let resp:string|null = null;
        resp = await gpt(token); 
        if (!state.abortController.signal.aborted && resp !== null) {
          if (rsp !== "") {
            synthesizeSpeech(resp);
          }
        }
        setprocessing(false);
        setStartTimer(true);
      }
    };
    if (sessionStopped && !micListening) {
      setStartTimer(false);
    }
    handleSessionStopped();
  }, [sessionStopped, micListening]);

  useEffect(() => {
    if (state.hitMaxLen && micListening) {
      setErrorText(
        "You are nearing the maximum word limit for the interview. Auto muting your mic in 20 seconds."
      );
      setshowApiError(true);
      micTimeout = setTimeout(() => stopMic(), 20000);
      return () => {
        if (micTimeout) {
          clearTimeout(micTimeout);
        }
      };
    }
  }, [state.hitMaxLen]);

  useEffect(() => {
    return () => {
      // Cleanup code (runs when the component unmounts or before it re-runs)
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync();
      }
    };
  }, [recognizer]);

  useEffect(() => {
    const fetchData = async () => {
      if (state.initialLoad) {
        window.location.href = "/";
      } else {
        const tokenObj = await getTokenOrRefresh();
        if (tokenObj.authToken && tokenObj.region) {
          const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
            tokenObj.authToken,
            tokenObj.region
          );
          speechConfig.speechRecognitionLanguage = "en-US";

          const audioConfig =
            speechsdk.AudioConfig.fromDefaultMicrophoneInput();
          setrecognizer(
            new speechsdk.SpeechRecognizer(speechConfig, audioConfig)
          );
        }
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (state.GPTCallCount === MAXCALLS - 1) {
      setErrorText("You have 1 more correspondence left.");
      setshowApiError(true);
    }
  }, [state.GPTCallCount]);

  useEffect(() => {
    if (!startTimer) {
      clearInterval(intInterval);
    } else {
      let interval1 = setInterval(() => {
        setTimer((prevTime) => prevTime + 1); // Increment time
      }, 1000);

      setIntInterval(interval1);
    }
    return () => clearInterval(intInterval);
  }, [startTimer]);

  useEffect(() => {
    updatesession({ time: timer });
  }, [timer]);

  useEffect(() => {
    if (micActivationTimeout !== null) {
      setmicListening(false);
    }
  }, [micActivationTimeout]);

  useEffect(() => {
    if (state.session.length > 0 && state.currentQuestion >= 0) {
      setTimer(state.retryPast ? 0 : state.session[state.currentQuestion].time);
    }
    if(state.isAssessment)
    {
      setStartTimer(true);
    }
  }, [state.currentQuestion]);

  useEffect(() => {
    const setupSpeechRecognition = async () => {
      if (micListening) {
        if (!state.abortController.signal.aborted && recognizer) {
          recognizer.startContinuousRecognitionAsync();
          setmicActivationTimeout(null);

          recognizer.canceled = (s, e) => {
            setErrorText("Mic activation failed. Retrying in 5 seconds.");
            setmicListening(false);
            setshowApiError(true);
            setmicActivationTimeout(
              setTimeout(() => {
                setmicListening(true);
                setErrorText(
                  "Encountered a problem reaching the api, retrying in 3 seconds..."
                );
                setshowApiError(false);
              }, 5000)
            );
          };
          recognizer.recognized = (s: any, e: any) => {
            if (e.result.reason === speechsdk.ResultReason.RecognizedSpeech) {
              setrsp((prev: string) => `${prev} ${e.result.text}`);
            }
          };
          recognizer.recognizing = (s: any, e: any) => {
            setrspLive(e.result.text);
          };
          recognizer.sessionStopped = async (s: any, e: any) => {
            setsessionStopped(true);
          };
        }
      } else if (micActivationTimeout == null) {
        setspeechLen(state.contextLength);
        //sethitMaxLen(false);
        setshowApiError(false);
        setErrorText(
          "Encountered a problem reaching the api, retrying in 3 seconds..."
        );
      }
    };
    setupSpeechRecognition();
  }, [micListening]);

  if (showLoader) return <FeedbackLoader />;

  return (
    <>
      <Nav />
      <div className="relative">
        {/* Light gray overlay for practice mode */}
        {state.isPractice && (
          <div className="absolute inset-0 bg-gray-200 bg-opacity-50 z-40 pointer-events-none"></div>
        )}
        <div className="overflow-hidden bg-white relative z-50">
          <ResizeFeedback
            timerOn={startTimer}
            displayPastFeedback={state.displayPastFeedback}
            retryClick={
              !state.displayPastFeedback
                ? () => {
                    setisRetry(true);
                    startFunc(state.currentQuestion, true);
                  }
                : () => {
                    navigateThenRun(() => {
                      setrole(state.session[0].role);
                      updatesession({
                        sessionId: generateGUID(),
                        type: state.session[state.currentQuestion].question.type,
                      });
                      settype(state.session[state.currentQuestion].question.type);
                      setQs(null, state.session[state.currentQuestion].question);
                      setqType("choose question");
                      setcurrentQuestion(0);
                      setretryPast(true);
                    }, "/interview");
                  }
            }
            nextClick={
              state.currentQuestion < state.session.length - 1
                ? () => startFunc(state.currentQuestion + 1, false, token)
                : undefined
            }
            org={{
              inOrg:
                state.profileUser &&
                state.profileUser.organization !== undefined &&
                state.profileUser.organization !== ""
                  ? true
                  : false,
              accessibleToOrg: state.pastInfo["orgAccessible"],
              updateAccessibleToOrg: (e) => updateAccessibleToOrg(e),
            }}
            adminView={isAdminView}
            micOpts={{
              stopMic,
              sttFromMic,
              micListening,
              processing: state.processing,
            }}
            synthesizeSpeech={synthesizeSpeech}
            setShowLoader={setShowLoader}
            recognizer={recognizer}
            newQuestionClick={(ind: number) => NewQuestionClick(ind)}
          />
        </div>
      </div>
    </>
  );
}

export default InterviewScreen;
